import { StatusCode } from '@/api/enumerations';
import { getOversightUsers } from '@/api/oversight';
import { OversightUsers } from '@/api/oversight/types';
import { Search } from '@/components/Search';
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import { IconCloseMS } from '@/constants/icons';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Info, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import NewAdministrativeUser from '../../components/NewAdministrativeUser';
import { Table, TableHeadData } from '../../components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '../../components/Table/styles';
import { RoundedGreen, TableDataHeader } from '../../styles';
import {
  buildParams,
  Filter,
  formatDate,
  Item,
  lastSessionComparator,
  normalizeStr,
} from '../../utils';
import {
  AmountOFClients,
  ChipFiltersContainer,
  DeleteFilterButton,
  FilterAndSearchContainer,
  FilterContainer,
  StyledChip,
  StyledSelect,
  TableDataName,
} from '../Clients/styles';
import { AdministrativeUsersTableRow } from './types';

const HeadRow = [
  { id: 1, label: 'Usuário Administrativo', key: 'name' },
  {
    id: 2,
    label: 'Última sessão',
    key: 'last_session_ended_at',
    comparator: lastSessionComparator<AdministrativeUsersTableRow>(
      (row) => row.online,
      (row) => row.last_session_ended_at
    ),
  },
  { id: 3, label: 'Acessos delegados', key: 'delegated_access_count' },
] satisfies TableHeadData<AdministrativeUsersTableRow>[];

export function AdministrativeUsers(): JSX.Element {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [usersData, setUsersData] = useState<OversightUsers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [filtersList, setFiltersList] = useState<Filter[]>([
    {
      id: 1,
      paramKey: 'role',
      label: 'Tipo de usuário',
      isActive: false,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'operator', label: 'Operador' },
      ],
    },
    {
      id: 2,
      paramKey: 'last_session_status',
      label: 'Última sessão',
      isActive: false,
      disabled: true,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'online', label: 'Online' },
        { value: 'active', label: 'Sessão delegada ativa' },
        { value: 'ended', label: 'Sessão encerrada' },
      ],
    },
    {
      id: 3,
      paramKey: 'delegated_access_status',
      label: 'Acesso delegado',
      isActive: false,
      disabled: true,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'pending_consent', label: 'Consentimento pendente' },
        { value: 'approved', label: 'Solicitação aprovada' },
        { value: 'active', label: 'Sessão delegada ativa' },
        { value: 'ended', label: 'Sessão encerrada' },
        { value: 'rejected', label: 'Solicitação negada' },
        { value: 'expired', label: 'Solicitação expirada' },
        { value: 'canceled', label: 'Solicitação cancelada' },
      ],
    },
  ]);

  const isActiveFilter = filtersList.some((filter) => filter.isActive);
  const queryParams = buildParams(filtersList, normalizeStr(debouncedSearch));

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const navigate = useNavigate();

  const handleDeleteAllFilters = (): void => {
    const newList = filtersList.map((filter) => {
      const defaultValue = filter.items[0].value;

      return {
        ...filter,
        isActive: false,
        items: filter.items.map((item) => ({
          ...item,
          active: item.value === defaultValue,
        })),
      };
    });

    setFiltersList(newList);
    setSearch('');
  };

  const handleActivateFilter = (filterId: number, itemValue: string): void => {
    const newFilterList = filtersList.map((filter) =>
      filter.id === filterId
        ? {
            ...filter,
            isActive: itemValue !== 'all',
            items: filter.items.map((item) => ({
              ...item,
              active: item.value === itemValue,
            })),
          }
        : filter
    );

    setFiltersList(newFilterList);
  };

  const handleClear = (event: React.MouseEvent, filterId: number): void => {
    event.stopPropagation();

    const newFilterList = filtersList.map((filter) => {
      if (filter.id !== filterId) {
        return filter;
      }

      const defaultValue = filter.items[0].value;

      return {
        ...filter,
        isActive: false,
        items: filter.items.map((item) => ({
          ...item,
          active: item.value === defaultValue,
        })),
      };
    });

    setFiltersList(newFilterList);
  };

  const renderLabel = (label: string): ReactNode | string => {
    if (label.includes('Acesso')) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
          <Typography color="secondary">{label}</Typography>
          <Tooltip
            title="
              Filtra os supervisores com ao menos um acesso delegado no estado
              selecionado. O número na coluna “Acessos delegados” reflete o
              total histórico do supervisor, independente do filtro aplicado.
            "
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'wrap',
                  maxWidth: '720px',
                },
              },
            }}
          >
            <Info
              color="disabled"
              sx={{ fontSize: '16px', margin: '4px', background: 'white' }}
            />
          </Tooltip>
        </Box>
      );
    }
    return label;
  };

  const handleToGoTo = (id: number): void =>
    navigate(`administrative-users/details/${id}`);

  const getUsers = useCallback(
    async (params: string) => {
      try {
        const { data, detail } = await getOversightUsers(params, page);

        if (detail?.description) throw new Error(detail?.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente');
        }

        if (data) {
          setUsersData(data.users);
          setRecords(data.records);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    // handleSnackbar e getErrorMessage são recriados a cada render; incluí-los
    // tornaria getUsers instável e o efeito abaixo dispararia em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page]
  );

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(debounceTimeout);
  }, [search]);

  useEffect(() => {
    getUsers(queryParams);
  }, [getUsers, queryParams]);

  const handleUserCreated = useCallback(
    (): Promise<void> => getUsers(queryParams),
    [getUsers, queryParams]
  );

  useEffect(() => {
    const interval = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    return () => clearTimeout(interval);
  }, []);

  return (
    <TableDataHeader>
      <FilterAndSearchContainer>
        <Search
          placeholder="Buscar por nome, empresa ou email..."
          value={search}
          setSearchField={setSearch}
          width="423px"
          maxLength={50}
        />

        {filtersList.map((filter) => {
          const selectedValue =
            filter.items.find((item: Item) => item.active)?.value ??
            filter.items[0].value;

          return (
            <FilterContainer>
              <InputLabel>{renderLabel(filter.label)}</InputLabel>

              <StyledSelect
                label={filter.label}
                value={selectedValue}
                disabled={filter.disabled}
                displayEmpty
                onChange={(e: SelectChangeEvent<unknown>) =>
                  handleActivateFilter(filter.id, e.target.value as string)
                }
              >
                {filter.items.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FilterContainer>
          );
        })}
      </FilterAndSearchContainer>

      <ChipFiltersContainer isVisible={isActiveFilter}>
        <Stack direction="row" spacing={1}>
          {filtersList.map((filter) =>
            filter.isActive ? (
              <StyledChip>
                {filter.label}:{' '}
                {filter.items.map(
                  (item) =>
                    item.active && (
                      <strong key={item.value}>{item.label}</strong>
                    )
                )}
                <DeleteFilterButton onClick={(e) => handleClear(e, filter.id)}>
                  {IconCloseMS}
                </DeleteFilterButton>
              </StyledChip>
            ) : null
          )}
        </Stack>
        {isActiveFilter && (
          <ButtonBase onClick={handleDeleteAllFilters}>
            Limpar todos os filtros {IconCloseMS}
          </ButtonBase>
        )}
      </ChipFiltersContainer>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <TableDataName>Usuários Administrativos</TableDataName>
          <AmountOFClients>
            {isLoading ? (
              <SkeletonWrapper
                isLoading={isLoading}
                variant="text"
                height={28}
                width={128}
              />
            ) : (
              `${records} usuários`
            )}
          </AmountOFClients>
        </div>
        <NewAdministrativeUser onUserCreated={handleUserCreated} />
      </div>

      <Table
        data={usersData}
        tableHeadData={HeadRow}
        onRowClick={(row) => handleToGoTo(row.id)}
        getRowId={(row) => row.id}
        emptyTableIcon={<SearchIcon />}
        emptyTableDescription="Nenhum resultado encontrado"
        records={records}
        page={page}
        setPage={setPage}
        isLoading={isLoading}
        defaultOrderBy="last_session_ended_at"
        defaultOrder="desc"
        renderRow={(row) => (
          <>
            <StyledTableCellBody>
              {row.name}

              <SubtitleCell>
                <span>{row.role}</span>
              </SubtitleCell>
            </StyledTableCellBody>
            <StyledTableCellBody>
              {row.online === true ? (
                <div>
                  <RoundedGreen />
                  Online
                </div>
              ) : (
                formatDate(row.last_session_ended_at, 'absolute')
              )}
            </StyledTableCellBody>
            <StyledTableCellBody>
              {row.delegated_access_count ? row.delegated_access_count : 0}
            </StyledTableCellBody>
          </>
        )}
      />
    </TableDataHeader>
  );
}
